import { ENGINE_VERSION, audioPath, ttsHash, type PresetId, type PublicConfig, type TtsAsset } from "@mantra/shared";
import { API_BASE, api } from "./api";
import { db, kvGet } from "./db";

/**
 * Клиентская часть кэша озвучки.
 * L0 — декодированные AudioBuffer в памяти, L1 — Cache Storage (то же имя, что у Service Worker),
 * дальше — сервер (CDN → S3 → синтез). Хэш считаем сами: попадание в L0/L1 не требует сети.
 */
const CACHE_NAME = "tts-audio-v1";
const MEM_LIMIT = 80;

export interface ClipRequest {
  text: string;
  voiceId: string;
  preset: PresetId;
}

export interface ResolvedClip extends ClipRequest {
  hash: string;
  url: string;
  durationMs: number;
}

const buffers = new Map<string, AudioBuffer>();
const hasCaches = () => typeof caches !== "undefined";

async function engineAndBase(): Promise<{ engine: string; base: string }> {
  const cfg = await kvGet<PublicConfig>("config");
  return { engine: cfg?.engineVersion ?? ENGINE_VERSION, base: cfg?.audioBaseUrl || API_BASE };
}

export async function clipUrl(hash: string): Promise<string> {
  return `${(await engineAndBase()).base}${audioPath(hash)}`;
}

async function cachedResponse(url: string): Promise<Response | undefined> {
  if (!hasCaches()) return undefined;
  const cache = await caches.open(CACHE_NAME);
  return cache.match(url);
}

async function download(url: string): Promise<Response> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`audio ${res.status}`);
  if (hasCaches()) {
    const cache = await caches.open(CACHE_NAME);
    await cache.put(url, res.clone());
  }
  return res;
}

/**
 * Гарантирует, что все клипы лежат локально. Сервер дёргаем только за промахами.
 * onProgress(готово, всего) — для индикатора «Озвучиваю 3 из 8».
 */
export async function ensureClips(items: ClipRequest[], onProgress?: (done: number, total: number) => void): Promise<ResolvedClip[]> {
  const { engine } = await engineAndBase();
  const withHash = await Promise.all(
    items.map(async (i) => ({ ...i, hash: await ttsHash(i.text, i.voiceId, i.preset, engine) })),
  );
  const unique = [...new Map(withHash.map((i) => [i.hash, i])).values()];
  const metas = await db.clips.bulkGet(unique.map((u) => u.hash));
  const resolved = new Map<string, ResolvedClip>();
  const missing: typeof unique = [];

  for (let i = 0; i < unique.length; i++) {
    const u = unique[i]!;
    const m = metas[i];
    if (m && (buffers.has(u.hash) || (await cachedResponse(m.url)))) resolved.set(u.hash, { ...u, url: m.url, durationMs: m.durationMs });
    else missing.push(u);
  }
  let done = resolved.size;
  onProgress?.(done, unique.length);

  // Личный голос синтезируется в фоне: сервер отвечает pending, мы повторяем запрос, пока всё не готово
  const POLL_MS = 2500;
  const DEADLINE_MS = 20 * 60_000;
  const started = Date.now();
  let pending = missing;
  while (pending.length) {
    const next: typeof missing = [];
    for (let i = 0; i < pending.length; i += 50) {
      const chunk = pending.slice(i, i + 50);
      const { items: assets } = await api<{ items: TtsAsset[] }>("/v1/tts/batch", {
        method: "POST",
        json: { items: chunk.map(({ text, voiceId, preset }) => ({ text, voiceId, preset })) },
      });
      await Promise.all(
        assets.map(async (a, idx) => {
          const req = chunk[idx]!;
          if (a.status === "pending") return void next.push(req);
          if (a.status !== "ready") throw new Error("Не удалось озвучить фразу. Попробуйте ещё раз.");
          const url = /^https?:\/\//.test(a.url) ? a.url : `${API_BASE}${a.url}`;
          await download(url);
          await db.clips.put({ hash: a.hash, url, durationMs: a.durationMs, cachedAt: Date.now() });
          resolved.set(req.hash, { ...req, hash: a.hash, url, durationMs: a.durationMs });
          onProgress?.(++done, unique.length);
        }),
      );
    }
    pending = next;
    if (!pending.length) break;
    if (Date.now() - started > DEADLINE_MS) throw new Error("Озвучка заняла слишком много времени. Попробуйте позже.");
    await new Promise((r) => setTimeout(r, POLL_MS));
  }
  return withHash.map((i) => resolved.get(i.hash)!);
}

/** Сколько фраз уже доступно офлайн — без сети и без запросов к серверу */
export async function offlineReadiness(items: ClipRequest[]): Promise<{ ready: number; total: number }> {
  const { engine } = await engineAndBase();
  const hashes = await Promise.all(items.map((i) => ttsHash(i.text, i.voiceId, i.preset, engine)));
  const metas = await db.clips.bulkGet(hashes);
  return { ready: metas.filter(Boolean).length, total: items.length };
}

export async function loadBuffer(ctx: BaseAudioContext, clip: { hash: string; url: string }): Promise<AudioBuffer> {
  const hit = buffers.get(clip.hash);
  if (hit) return hit;
  const res = (await cachedResponse(clip.url)) ?? (await download(clip.url));
  const buf = await ctx.decodeAudioData(await res.arrayBuffer());
  buffers.set(clip.hash, buf);
  if (buffers.size > MEM_LIMIT) buffers.delete(buffers.keys().next().value as string);
  return buf;
}

export async function clearAudioCache() {
  buffers.clear();
  await db.clips.clear();
  if (hasCaches()) await caches.delete(CACHE_NAME);
}

export async function audioCacheSize(): Promise<{ clips: number; bytes: number | null }> {
  const clips = await db.clips.count();
  const est = await (navigator.storage?.estimate?.() ?? Promise.resolve(undefined)).catch(() => undefined);
  return { clips, bytes: est?.usage ?? null };
}
